import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { useSEO, useSchemaLD } from '@/hooks/useSEO';

const sitemapSections = [
  {
    title: 'Páginas Principais',
    icon: 'ri-home-line',
    links: [
      { label: 'Home', path: '/', desc: 'Página inicial da Home Office Premium' },
      { label: 'Produtos', path: '/produtos', desc: 'Catálogo completo de móveis corporativos' },
      { label: 'Marcas', path: '/marcas', desc: 'Todas as marcas parceiras' },
      { label: 'Cases de Sucesso', path: '/cases', desc: 'Portfólio de projetos realizados' },
      { label: 'Catálogos', path: '/catalogos', desc: 'Download de catálogos em PDF' },
      { label: 'Blog', path: '/blog', desc: 'Artigos sobre ergonomia e escritório' },
      { label: 'Contato', path: '/contato', desc: 'Solicite orçamento ou fale conosco' },
    ],
  },
  {
    title: 'Categorias de Produtos',
    icon: 'ri-layout-grid-line',
    links: [
      { label: 'Cadeiras Ergonômicas', path: '/cadeiras', desc: 'Cadeiras Frisokar e Plaxmetal' },
      { label: 'Mesas Corporativas', path: '/mesas', desc: 'Mesas Marzo Vitorino' },
      { label: 'Divisórias Modulares', path: '/divisorias', desc: 'Divisórias para escritório' },
      { label: 'Cabines Acústicas', path: '/cabines-acusticas', desc: 'Cabines para privacidade e foco' },
      { label: 'Mobiliário Escolar', path: '/escolar', desc: 'Móveis para escolas e universidades' },
      { label: 'Mobiliários de Aço', path: '/aco', desc: 'Armários, estantes e lockers W3' },
    ],
  },
  {
    title: 'Marcas Parceiras',
    icon: 'ri-award-line',
    links: [
      { label: 'Frisokar', path: '/frisokar', desc: 'Cadeiras ergonômicas certificadas NR17' },
      { label: 'Plaxmetal', path: '/plaxmetal', desc: 'Cadeiras corporativas de alta durabilidade' },
      { label: 'Marzo Vitorino', path: '/marzo-vitorino', desc: 'Mesas e estações de trabalho' },
      { label: 'Pandim', path: '/pandim', desc: 'Mobiliário corporativo completo' },
      { label: 'W3 Indústria', path: '/w3-industria', desc: 'Mobiliário de aço NR18 e NR24' },
      { label: 'Rivatti', path: '/rivatti', desc: 'Móveis para food service e outdoor' },
    ],
  },
  {
    title: 'Serviços',
    icon: 'ri-tools-line',
    links: [
      { label: 'Marcenaria Personalizada', path: '/marcenaria', desc: 'Móveis planejados sob medida' },
      { label: 'Turnkey — Obras Completas', path: '/turnkey', desc: 'Do projeto ao mobiliário, chave na mão' },
      { label: 'Área de Atendimento', path: '/area-de-atendimento', desc: '+100 cidades em Pernambuco' },
      { label: 'Móveis Corporativos (LP)', path: '/lp', desc: 'Landing page de móveis corporativos' },
    ],
  },
  {
    title: 'Materiais Gratuitos',
    icon: 'ri-book-open-line',
    links: [
      { label: 'Ebooks Gratuitos', path: '/ebooks', desc: 'Guias práticos sobre ergonomia e escritórios' },
      { label: 'Guia NR-17: Ergonomia no Escritório', path: '/ebooks', desc: 'Checklist completo de conformidade trabalhista' },
      { label: 'Montando seu Escritório do Zero', path: '/ebooks', desc: 'Do planejamento ao layout final' },
      { label: 'A Recepção que Vende', path: '/ebooks', desc: 'Como criar experiências que fidelizam clientes' },
      { label: 'Escritório de Advocacia que Fecha Contratos', path: '/ebooks', desc: 'Psicologia do ambiente jurídico e honorários' },
    ],
  },
  {
    title: 'Institucional',
    icon: 'ri-information-line',
    links: [
      { label: 'Política de Privacidade', path: '/privacidade', desc: 'Termos e política de dados' },
      { label: 'Mapa do Site', path: '/sitemap', desc: 'Todas as páginas do site' },
    ],
  },
];

export default function SitemapPage() {
  useSEO({
    title: 'Mapa do Site | Home Office Premium – Caruaru PE',
    description: 'Mapa do site da Home Office Premium em Caruaru PE. Encontre rapidamente todas as páginas: produtos, marcas, serviços, blog e contato.',
    keywords: 'mapa site Home Office Premium, navegação, páginas, sitemap',
    path: '/sitemap',
    noindex: true,
  });

  useSchemaLD({
    id: 'schema-sitemap',
    schema: {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: 'Mapa do Site – Home Office Premium',
      url: `${import.meta.env.VITE_SITE_URL ?? 'https://homeofficepremium.com'}/sitemap`,
    },
  });

  useSchemaLD({
    id: 'schema-sitemap-breadcrumb',
    schema: {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: import.meta.env.VITE_SITE_URL ?? 'https://homeofficepremium.com' },
        { '@type': 'ListItem', position: 2, name: 'Mapa do Site', item: `${import.meta.env.VITE_SITE_URL ?? 'https://homeofficepremium.com'}/sitemap` },
      ],
    },
  });

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <div className="pt-20">
        <div className="bg-[#f8f8f6] py-14">
          <div className="max-w-7xl mx-auto px-6">
            <nav className="flex items-center gap-2 text-xs text-gray-400 mb-5">
              <Link to="/" className="hover:text-[#E8610A] transition-colors">Home</Link>
              <i className="ri-arrow-right-s-line"></i>
              <span className="text-gray-600">Mapa do Site</span>
            </nav>
            <p className="text-[#E8610A] text-xs font-semibold uppercase tracking-widest mb-3">Navegação</p>
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-3">Mapa do Site</h1>
            <p className="text-gray-500 text-base max-w-xl">
              Encontre rapidamente qualquer página do site da Home Office Premium. Todas as categorias, marcas, serviços e conteúdos organizados em um só lugar.
            </p>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-6 py-16">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {sitemapSections.map((section) => (
              <div key={section.title} className="bg-[#fafaf8] rounded-2xl p-7 border border-gray-100">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 flex items-center justify-center bg-[#E8610A]/10 rounded-xl flex-shrink-0">
                    <i className={`${section.icon} text-[#E8610A] text-lg`}></i>
                  </div>
                  <h2 className="text-gray-900 font-bold text-base">{section.title}</h2>
                </div>
                <ul className="space-y-3">
                  {section.links.map((link) => (
                    <li key={link.path}>
                      <Link
                        to={link.path}
                        className="group flex items-start gap-3 hover:bg-white rounded-xl p-2 -mx-2 transition-colors duration-150 cursor-pointer"
                      >
                        <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                          <i className="ri-arrow-right-s-line text-[#E8610A] text-sm group-hover:translate-x-0.5 transition-transform"></i>
                        </div>
                        <div>
                          <span className="text-gray-800 text-sm font-medium group-hover:text-[#E8610A] transition-colors block leading-snug">
                            {link.label}
                          </span>
                          <span className="text-gray-400 text-xs leading-snug">{link.desc}</span>
                        </div>
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          <div className="mt-12 bg-[#E8610A] rounded-2xl p-8 flex flex-col md:flex-row items-center justify-between gap-6">
            <div>
              <h3 className="text-white font-bold text-lg mb-1">Não encontrou o que procura?</h3>
              <p className="text-white/70 text-sm">Fale com nossa equipe pelo WhatsApp ou envie uma mensagem.</p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 flex-shrink-0">
              <a
                href="https://wa.me/5581982030285"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-white text-[#E8610A] text-sm font-semibold px-6 py-3 rounded-full hover:bg-gray-100 transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-whatsapp-line"></i>
                WhatsApp
              </a>
              <Link
                to="/contato"
                className="inline-flex items-center gap-2 bg-white/10 border border-white/30 text-white text-sm font-semibold px-6 py-3 rounded-full hover:bg-white/20 transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-mail-line"></i>
                Contato
              </Link>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}
